import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AppContext';
import API from '../utils/api';
import toast from 'react-hot-toast';

const WIDGET_ID =
  process.env.REACT_APP_MSG91_WIDGET_ID || '3666746f6b78393939393334';

const TOKEN_AUTH =
  process.env.REACT_APP_MSG91_TOKEN_AUTH || '536046TqQlDzxHDW6a36af0dP1';

export default function Login() {
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [requestId, setRequestId] = useState('');

  const otpLock = useRef(false);
  const lastOtpTime = useRef(0);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    otp: '',
  });

  useEffect(() => {
    const initMsg91 = () => {
      if (typeof window.initSendOTP !== 'function') return;

      window.initSendOTP({
        widgetId: WIDGET_ID,
        tokenAuth: TOKEN_AUTH,
        identifier: '',
        exposeMethods: true,
        captchaRenderId: 'msg91-captcha',
        success: (data) => console.log('MSG91 global success:', data),
        failure: (err) => console.log('MSG91 global failure:', err),
      });

      window.__MSG91_READY__ = true;
      console.log('MSG91 initialized');
    };

    if (!document.getElementById('msg91-sdk')) {
      const script = document.createElement('script');
      script.id = 'msg91-sdk';
      script.src = 'https://verify.msg91.com/otp-provider.js';
      script.async = true;
      script.onload = initMsg91;
      document.body.appendChild(script);
    } else {
      initMsg91();
    }
  }, []);

  const extractToken = (data) => {
    console.log('OTP verified full response:', data);

    return (
      data?.accessToken ||
      data?.access_token ||
      data?.token ||
      data?.jwt ||
      data?.authToken ||
      data?.message ||
      data?.data?.accessToken ||
      data?.data?.access_token ||
      data?.data?.token ||
      data?.data?.jwt ||
      data?.data?.message ||
      data?.response?.accessToken ||
      data?.response?.access_token ||
      data?.response?.token ||
      data?.response?.jwt
    );
  };

  const updateField = (name, value) => {
    if (name === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
      setOtpSent(false);
      setOtpVerified(false);
      setAccessToken('');
      setRequestId('');
      setForm((prev) => ({ ...prev, phone: value, otp: '' }));
      return;
    }

    if (name === 'otp') {
      value = value.replace(/\D/g, '').slice(0, 6);
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setLoading(false);
    setOtpLoading(false);
    setOtpSent(false);
    setOtpVerified(false);
    setAccessToken('');
    setRequestId('');
    setShowPassword(false);
    otpLock.current = false;

    setForm({
      name: '',
      phone: '',
      email: '',
      password: '',
      otp: '',
    });
  };

  const sendOtp = () => {
    const now = Date.now();

    if (!form.phone || form.phone.length !== 10) {
      return toast.error('Enter valid 10-digit mobile number');
    }

    if (!WIDGET_ID || !TOKEN_AUTH) {
      return toast.error('MSG91 widget configuration missing');
    }

    if (!window.__MSG91_READY__ || typeof window.sendOtp !== 'function') {
      return toast.error('OTP service loading. Try again in 2 seconds.');
    }

    if (otpLock.current || otpLoading) return;

    if (now - lastOtpTime.current < 15000) {
      return toast.error('Please wait before requesting OTP again');
    }

    otpLock.current = true;
    lastOtpTime.current = now;
    setOtpLoading(true);

    window.sendOtp(
      `91${form.phone}`,
      (data) => {
        console.log('OTP sent response:', data);

        const reqId =
          data?.requestId ||
          data?.request_id ||
          data?.reqId ||
          data?.message ||
          data?.data?.requestId ||
          data?.data?.request_id;

        setRequestId(reqId || '');
        setOtpSent(true);
        setOtpVerified(false);
        setAccessToken('');
        toast.success('OTP sent successfully');

        otpLock.current = false;
        setOtpLoading(false);
      },
      (error) => {
        console.log('OTP send error:', error);
        toast.error(error?.message || 'Failed to send OTP');

        otpLock.current = false;
        setOtpLoading(false);
      }
    );
  };

  const verifyOtp = () => {
    if (!form.otp || form.otp.length < 4) {
      return toast.error('Enter valid OTP');
    }

    if (typeof window.verifyOtp !== 'function') {
      return toast.error('OTP verification service not ready');
    }

    setOtpLoading(true);

    window.verifyOtp(
      Number(form.otp),
      (data) => {
        const token = extractToken(data);

        if (!token) {
          setOtpVerified(false);
          setAccessToken('');
          setOtpLoading(false);
          toast.error('OTP verified but access token missing. Check console.');
          return;
        }

        setAccessToken(token);
        setOtpVerified(true);
        setOtpLoading(false);
        toast.success('Mobile number verified');
      },
      (error) => {
        console.log('OTP verify error:', error);

        setOtpVerified(false);
        setAccessToken('');
        setOtpLoading(false);

        if (error?.code === 703 || String(error?.message || '').toLowerCase().includes('already')) {
          toast.error('This OTP was already used. Please resend OTP.');
          return;
        }

        toast.error(error?.message || 'Invalid OTP');
      },
      requestId || undefined
    );
  };

  const login = async () => {
    if (!form.phone || !form.password) {
      return toast.error('Enter phone and password');
    }

    if (form.phone.length !== 10) {
      return toast.error('Enter valid 10-digit mobile number');
    }

    setLoading(true);

    try {
      const { data } = await API.post('/auth/login', {
        phone: form.phone,
        password: form.password,
      });

      loginUser(data.user, data.token);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const register = async () => {
    if (!form.name || !form.phone || !form.password) {
      return toast.error('Name, phone and password required');
    }

    if (form.phone.length !== 10) {
      return toast.error('Enter valid 10-digit mobile number');
    }

    if (!otpVerified || !accessToken) {
      return toast.error('Please verify mobile number with OTP');
    }

    setLoading(true);

    try {
      const { data } = await API.post('/auth/register', {
        name: form.name,
        phone: form.phone,
        email: form.email,
        password: form.password,
        accessToken,
      });

      loginUser(data.user, data.token);
      toast.success('Account created successfully');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!form.phone || !form.password) {
      return toast.error('Phone and new password required');
    }

    if (form.phone.length !== 10) {
      return toast.error('Enter valid 10-digit mobile number');
    }

    if (!otpVerified || !accessToken) {
      return toast.error('Please verify mobile number with OTP');
    }

    setLoading(true);

    try {
      await API.post('/auth/reset-password', {
        phone: form.phone,
        password: form.password,
        accessToken,
      });

      toast.success('Password reset successfully');
      switchMode('login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-enter" style={{ maxWidth: 460, margin: '80px auto', padding: '0 24px' }}>
      <div id="msg91-captcha" />

      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: 40, textAlign: 'center' }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, color: 'var(--maroon)', marginBottom: 6 }}>
          ✦ Anvith Dazzle
        </div>

        <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 28 }}>
          {mode === 'login' && 'Login to your account'}
          {mode === 'register' && 'Create your account'}
          {mode === 'forgot' && 'Reset your password'}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 26 }}>
          {[
            ['login', 'Login'],
            ['register', 'Register'],
            ['forgot', 'Forgot'],
          ].map(([key, label]) => (
            <button
              type="button"
              key={key}
              onClick={() => switchMode(key)}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: 30,
                border: '1px solid var(--border)',
                background: mode === key ? 'var(--maroon)' : 'white',
                color: mode === key ? 'white' : 'var(--text-mid)',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'register' && (
          <div className="form-group" style={{ textAlign: 'left' }}>
            <label className="form-label">Full Name *</label>
            <input
              className="form-input"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Enter your name"
            />
          </div>
        )}

        <div className="form-group" style={{ textAlign: 'left' }}>
          <label className="form-label">Mobile Number *</label>
          <input
            className="form-input"
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder="99999 99999"
          />
        </div>

        {mode === 'register' && (
          <div className="form-group" style={{ textAlign: 'left' }}>
            <label className="form-label">Email Optional</label>
            <input
              className="form-input"
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="your@email.com"
            />
          </div>
        )}

        <div className="form-group" style={{ textAlign: 'left' }}>
          <label className="form-label">
            {mode === 'forgot' ? 'New Password *' : 'Password *'}
          </label>

          <div style={{ position: 'relative' }}>
            <input
              className="form-input"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => updateField('password', e.target.value)}
              placeholder="Minimum 6 characters"
              onKeyDown={(e) => e.key === 'Enter' && mode === 'login' && login()}
              style={{ paddingRight: 50 }}
            />

            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: '#8a7b7b',
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {(mode === 'register' || mode === 'forgot') && (
          <>
            <button
              type="button"
              className="btn btn-outline btn-full"
              onClick={sendOtp}
              disabled={otpLoading || otpVerified}
              style={{ marginBottom: 12 }}
            >
              {otpVerified ? '✅ OTP Verified' : otpLoading ? 'Sending OTP...' : otpSent ? 'Resend OTP' : 'Send OTP'}
            </button>

            {otpSent && !otpVerified && (
              <div className="form-group" style={{ textAlign: 'left' }}>
                <label className="form-label">Enter OTP *</label>
                <input
                  className="form-input"
                  type="tel"
                  inputMode="numeric"
                  value={form.otp}
                  onChange={(e) => updateField('otp', e.target.value)}
                  placeholder="Enter OTP"
                />

                <button
                  type="button"
                  className="btn btn-primary btn-full"
                  onClick={verifyOtp}
                  disabled={otpLoading}
                  style={{ marginTop: 12 }}
                >
                  {otpLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            )}
          </>
        )}

        {mode === 'login' && (
          <button type="button" className="btn btn-primary btn-full btn-lg" onClick={login} disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        )}

        {mode === 'register' && (
  <button
    type="button"
    className="btn btn-primary btn-full btn-lg"
    onClick={() => {
      console.log("CREATE ACCOUNT CLICKED", { otpVerified, accessToken });
      register();
    }}
    disabled={loading}
  >
    {loading ? 'Creating...' : 'Create Account'}
  </button>
)}
        {mode === 'forgot' && (
          <button type="button" className="btn btn-primary btn-full btn-lg" onClick={resetPassword} disabled={loading || !otpVerified}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid var(--border)' }} />
          <span style={{ fontSize: 12, color: 'var(--text-light)' }}>or</span>
          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid var(--border)' }} />
        </div>

        <button type="button" className="btn btn-outline btn-full" onClick={() => navigate('/admin/login')}>
          🔐 Admin Login
        </button>
      </div>
    </div>
  );
}